

/**
 * ShareCardButton
 *
 * "Share My Picks" CTA on an undercard entry page. Opens a modal with the
 * generated Prediction Card preview, copy-link, and native-share trigger.
 *
 * Note: this only generates and surfaces the card. Claiming the
 * SHARE_PREDICTION_CARD reward happens separately in /earn — the user submits
 * the URL of their resulting post via the SubmitURLModal flow there. We could
 * inline the claim flow here later, but the URL-submission step makes more
 * sense after they've actually shared.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Share2, Link as LinkIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ShareCardButtonProps {
  /** UndercardEntry.id — the entry whose picks the card summarizes */
  undercardEntryId: string;
  /** Optional override of the card variant — 'prediction' (default) or 'winner' */
  variant?: 'prediction' | 'winner';
  /** Disabled state — e.g. event not yet entered */
  disabled?: boolean;
  className?: string;
}

export function ShareCardButton({
  undercardEntryId,
  variant = 'prediction',
  disabled,
  className,
}: ShareCardButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const cardUrl = variant === 'winner'
    ? `/api/cards/winner/${undercardEntryId}`
    : `/api/cards/prediction/${undercardEntryId}`;

  // Absolute URL for copy/share
  const [absoluteCardUrl, setAbsoluteCardUrl] = useState<string>(cardUrl);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setAbsoluteCardUrl(`${window.location.origin}${cardUrl}`);
  }, [cardUrl]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(absoluteCardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [absoluteCardUrl]);

  const handleShare = useCallback(() => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      void navigator
        .share({
          title: variant === 'winner' ? 'My winning picks on FantasyPRIME' : 'My picks on FantasyPRIME',
          text: 'Check out my picks',
          url: absoluteCardUrl,
        })
        .catch(() => {
          // User cancelled native share — no-op
        });
    } else {
      handleCopy();
    }
  }, [absoluteCardUrl, variant, handleCopy]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] transition-all',
          disabled
            ? 'bg-surface-3 text-warm-gray/40 cursor-not-allowed'
            : 'bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25',
          className,
        )}
        aria-haspopup="dialog"
      >
        <Share2 className="w-3.5 h-3.5" />
        Share My Picks
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-sm rounded-xl bg-surface-2 border border-surface-3 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-surface-3">
              <span className="text-xs font-bold text-cream">
                {variant === 'winner' ? 'Winner Card' : 'Prediction Card'}
              </span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-warm-gray/60 hover:text-cream"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Card preview — uses the generator route directly. ImageResponse
                returns a PNG so this is a one-shot render. */}
            <div className="bg-surface p-3 flex justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={cardUrl}
                alt={variant === 'winner' ? 'Winner card preview' : 'Prediction card preview'}
                width={1080}
                height={1350}
                className="w-full h-auto rounded-lg"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>

            <div className="p-3 space-y-2 border-t border-surface-3">
              <button
                onClick={handleShare}
                className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 text-xs font-bold"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share
              </button>
              <button
                onClick={handleCopy}
                className={cn(
                  'w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-bold transition-colors',
                  copied
                    ? 'bg-verdigris/15 text-verdigris border-verdigris/30'
                    : 'bg-surface-3 text-cream border-surface-4 hover:border-surface-4',
                )}
              >
                <LinkIcon className="w-3.5 h-3.5" />
                {copied ? 'Link copied!' : 'Copy image link'}
              </button>
              <p className="text-[10px] text-warm-gray/40 text-center mt-2 leading-relaxed">
                Post the card on X, Instagram, or TikTok and tag <strong>@FantasyPRIME</strong>.<br />
                Then paste your post URL on the <a href="/earn" className="text-gold hover:underline">Earn page</a> to claim coins.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ShareCardButton;
